import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import {
  Delivery,
  DepotLocation,
  HireItem,
  Item,
  Product
} from '../../../models';
import {
  CombinedCalendarDataPayload,
  CombinedCalendarItem,
  EventType,
  MetaData
} from '../../../models/combined-calendar';
import { CalendarEvent, CalendarEventAction } from 'angular-calendar';
import { add, endOfDay, startOfDay, sub } from 'date-fns';
import { map, mapTo, mergeMap, tap } from 'rxjs/operators';
import { ProductCategory } from '../../../models/product-category';
import { BookingItem } from '../../../models/booking-item';
import { ConfigService } from '../../../shared/services/config.service';
import { combineLatest, Observable, of } from 'rxjs';
import { DashboardService } from '../../../shared/services/dashboard.service';
import { ProductService } from '../../../shared';
import { ProductCategoryService } from '../../../shared/services/product-category.service';
import { BookingItemNamePipe } from '../../../shared/pipes/booking-item-name.pipe';

interface SelectedEvent {
  type: EventType;
  id: number;
}

export interface CombinedCalendarState {
  calendarDate: Date;
  events: CalendarEvent<MetaData<any>>[];
  selectedEvent: SelectedEvent | null;
  filter: {
    mode: 'all' | 'transport';
    category: number | null;
    product: number | null;
    location: number | null;
    available: boolean;
    hires: boolean;
    bookings: boolean;
    overdueOnly: boolean;
  };
  productCache: Map<number, ProductCacheItem>;
  categoryCache: Map<number, CategoryCacheItem>;
  locations: DepotLocation[];
}

export type ProductCacheItem = Pick<
  Product,
  'id' | 'name' | 'product_category_id'
>;
export type CategoryCacheItem = Pick<ProductCategory, 'id' | 'name'>;

const defaultState: CombinedCalendarState = {
  calendarDate: new Date(),
  events: [],
  selectedEvent: null,
  filter: {
    mode: 'all',
    category: null,
    product: null,
    location: null,
    overdueOnly: false,
    available: true,
    hires: true,
    bookings: true
  },
  productCache: new Map<number, ProductCacheItem>(),
  categoryCache: new Map<number, CategoryCacheItem>(),
  locations: []
};

const AVAILABLE_COLOR = {
  primary: '#237C3E',
  secondary: '#e9fff3'
};
const BOOKING_COLOR = {
  primary: '#c98200',
  secondary: '#ffe8c6'
};
const HIRE_COLOR = {
  primary: '#830909',
  secondary: '#ffc6c6'
};

function getBookingItemLocation(item: BookingItem) {
  if (item.item) {
    return parseInt(item.item.location, 10);
  }
  return 0;
}

@Injectable()
export class CombinedCalendarStore extends ComponentStore<CombinedCalendarState> {
  private order: EventType[] = ['bookingItem', 'hireItem', 'available'];
  readonly filteredEvents$ = this.select(({ events, filter, productCache }) => {
    return events
      .sort(({ title: a }, { title: b }) => a.localeCompare(b))
      .filter((event: CalendarEvent) => {
        if (
          filter.mode === 'transport' &&
          event.meta.transportJobs.length === 0
        ) {
          return false;
        }
        if (
          filter.category &&
          this.getCalendarEventCategory(event, productCache) !== filter.category
        ) {
          return false;
        }
        if (
          filter.location &&
          this.getCalendarEventLocation(event) !== filter.location
        ) {
          return false;
        }
        if (filter.overdueOnly === true && event.meta.overdue === false) {
          return false;
        }
        if (filter.available === false && event.meta.type === 'product') {
          return false;
        }
        if (filter.hires === false && event.meta.type === 'hireItem') {
          return false;
        }
        if (filter.bookings === false && event.meta.type === 'bookingItem') {
          return false;
        }
        return true;
      });
  });

  readonly calendarDate$ = this.select(({ calendarDate }) => calendarDate);

  readonly productCache$ = this.select(({ productCache }) => productCache);

  readonly categoryCache$ = this.select(({ categoryCache }) => categoryCache);
  readonly categories$ = this.select(({ categoryCache }) =>
    Array.from(categoryCache.values()).sort(({ name: a }, { name: b }) =>
      a.localeCompare(b)
    )
  );

  readonly setEvents = this.updater(
    (
      state,
      { active, bookings, availableItems }: CombinedCalendarDataPayload
    ) => {
      const locationMap = state.locations.reduce(
        (acc, curr) => acc.set(curr.id, curr.name),
        new Map<number, string>()
      );
      const activeEvents = active.map((event) =>
        this.mapeEventToCalendarEvent(event, locationMap, HIRE_COLOR)
      );
      const bookingEvents = bookings.map((booking) =>
        this.mapeEventToCalendarEvent(booking, locationMap, BOOKING_COLOR)
      );

      const startOfToday = startOfDay(new Date()).getTime();
      const futureActiveEvents = activeEvents.filter(
        (event) => event.end.getTime() >= startOfToday
      );
      const availableGroupedByProduct =
        this.availableGroupedByProductEventPerDay(
          availableItems,
          futureActiveEvents,
          bookingEvents,
          state.productCache
        );
      // given active and booking events, iterate through them and fill in the available gaps for the items in them - done
      // then iterate around avaliable items and crate events for them - done
      // then iterate around all available items and tentatively allocate items from unallocated - todo
      // const tentativelAllocatedAndAvailable = this.tentativelyAllocateItems(
      //   unallocated,
      //   [...availableFillers, ...available]
      // );
      return {
        ...state,
        events: [
          ...activeEvents,
          ...bookingEvents,
          ...availableGroupedByProduct
        ]
      };
    }
  );

  readonly setFilter = this.updater(
    (state, filter: Partial<typeof state.filter>) => ({
      ...state,
      filter: { ...state.filter, ...filter }
    })
  );

  readonly setCache = this.updater(
    (
      state,
      caches: {
        products: Map<number, ProductCacheItem>;
        categories: Map<number, CategoryCacheItem>;
      }
    ) => {
      return {
        ...state,
        productCache: caches.products,
        categoryCache: caches.categories
      };
    }
  );

  readonly setLocations = this.updater((state, locations: DepotLocation[]) => ({
    ...state,
    locations
  }));

  readonly setMode = this.updater((state, mode: 'all' | 'transport') => {
    return {
      ...state,
      filter: {
        ...state.filter,
        mode
      }
    };
  });

  readonly setCategory = this.updater((state, categoryId: number) => {
    return {
      ...state,
      filter: {
        ...state.filter,
        category: categoryId
      }
    };
  });

  readonly setSelectedEvent = this.updater(
    (state, selectedEvent: SelectedEvent | null) => ({
      ...state,
      selectedEvent
    })
  );
  readonly updateCalendarDate = this.updater((state, change: 1 | -1) => ({
    ...state,
    calendarDate: add(state.calendarDate, { weeks: change })
  }));
  readonly viewCurrentWeek = this.updater((state) => ({
    ...state,
    calendarDate: new Date()
  }));

  private readonly getData = this.effect(() => {
    return this.dashboardService.getCombinedCalendarData().pipe(
      mergeMap((res) => this.getProductCategoryCache(res)),
      tap({
        next: (
          data: CombinedCalendarDataPayload & {
            productCache: Map<number, ProductCacheItem>;
            categoryCache: Map<number, CategoryCacheItem>;
          }
        ) => {
          this.setEvents(data);
          this.setCache({
            products: data.productCache,
            categories: data.categoryCache
          });
        }
      })
    );
  });

  constructor(
    private configService: ConfigService,
    private dashboardService: DashboardService,
    private productService: ProductService,
    private categoryService: ProductCategoryService,
    private bookingItemNamePipe: BookingItemNamePipe
  ) {
    super(defaultState);
    this.configService.locations.subscribe({
      next: (locations) => this.setState((state) => ({ ...state, locations }))
    });
    this.getData();
  }

  private getProductCategoryCache(res: CombinedCalendarDataPayload) {
    const products = (res.active as CombinedCalendarItem<HireItem>[])
      .map((event) => ({
        id: event.data.item.item!.product!.id,
        name: event.data.item!.item!.product!.name,
        product_category_id: event.data.item.item!.product!.product_category_id
      }))
      .concat(
        res.bookings
          .filter((event) => event.data.item.product !== null)
          .map((bookingItemEvent) => ({
            id: bookingItemEvent.data.item.product_id,
            name: bookingItemEvent.data.item.product.name,
            product_category_id: bookingItemEvent.data.item.category_id
          }))
      );
    const categories = res.active
      .filter((event) => event.data.item.item.product.category !== null)
      .map((activeEvents) => ({
        id: activeEvents.data.item.item!.product!.category!.id,
        name: activeEvents.data.item.item!.product!.category!.name
      }))
      .concat(
        res.bookings
          .filter((event) => event.data.item.category !== null)
          .map((bookingEvents) => ({
            id: bookingEvents.data.item!.category!.id,
            name: bookingEvents.data.item!.category!.name
          }))
      )
      .filter((id) => !!id)
      .concat([{ id: -1, name: 'Uncategorised' }]);
    return this.cacheProductsAndCategories(
      products,
      categories,
      res.availableItems
    ).pipe(map((cache) => ({ ...cache, ...res })));
  }

  availableGroupedByProductEventPerDay(
    allAvalilableItems: Item[],
    futureActiveEvents: CalendarEvent<MetaData<HireItem>>[],
    bookingEvents: CalendarEvent<MetaData<BookingItem>>[],
    productCache: Map<number, ProductCacheItem>
  ) {
    const availableByProductId = allAvalilableItems.reduce(
      (acc, curr) => {
        const id = curr.product_id;
        if (acc[id]) {
          acc[id].push(curr);
          return acc;
        }
        acc[id] = [curr];
        return acc;
      },
      {} as Record<number, Item[]>
    );

    const futureEventsByProductId = futureActiveEvents.reduce(
      (
        acc,
        {
          start,
          end,
          meta: {
            item: {
              item: { product_id, id }
            }
          }
        }
      ) => {
        if (acc[product_id]) {
          acc[product_id].push({ start, end, item_id: id });
          return acc;
        }
        acc[product_id] = [{ start, end, item_id: id }];
        return acc;
      },
      {} as Record<number, { start: Date; end: Date; item_id: number | null }[]>
    );

    const bookingAndFutureEventsByProductId = bookingEvents.reduce(
      (
        acc,
        {
          start,
          end,
          meta: {
            item: { product_id, item_id, quantity }
          }
        }
      ) => {
        if (!product_id) {
          return acc;
        }
        const events = new Array(quantity).fill({ start, end, item_id });
        if (acc[product_id]) {
          acc[product_id].push(...events);
          return acc;
        }
        acc[product_id] = [...events];
        return acc;
      },
      futureEventsByProductId
    );

    const eventsGroupedByProduct = Object.entries(availableByProductId).flatMap(
      ([id, items]) => {
        let currentDate = startOfDay(new Date());
        const end = add(currentDate, { weeks: 4 });
        const productEvents = [];
        const product = productCache.get(parseInt(id, 10));
        if (!product) {
          return [];
        }
        while (currentDate.getTime() <= end.getTime()) {
          const nextDay = add(currentDate, { days: 1 });
          const conflictingEvents = bookingAndFutureEventsByProductId[id]
            ? bookingAndFutureEventsByProductId[id].filter(({ start, end }) => {
                return (
                  start.getTime() <= currentDate.getTime() &&
                  end.getTime() >= currentDate.getTime()
                );
              })
            : [];

          if (conflictingEvents.length > 0) {
            const availableItems = items.filter(
              ({ id }) =>
                conflictingEvents.map(({ item_id }) => item_id).indexOf(id) ===
                -1
            );
            productEvents.push(
              this.createProductEvent(
                currentDate,
                endOfDay(currentDate),
                product,
                availableItems.map(({ id }) => id),
                `${items.length - conflictingEvents.length} x ${product?.name ?? 'Unknown: ' + id}`
              )
            );
          } else {
            productEvents.push(
              this.createProductEvent(
                currentDate,
                endOfDay(currentDate),
                product,
                items.map(({ id }) => id),
                `${items.length} x ${product?.name ?? 'Unknown: ' + id}`
              )
            );
          }
          currentDate = nextDay;
        }
        const mergedDaysWithSameAvailability = productEvents.reduce(
          (acc, curr, index) => {
            // check that this item has a quantity of greater than 0, to omit any that have 0 items
            if (parseInt(curr.title.split(' ')[0], 10) < 1) {
              return acc;
            }

            if (acc.length === 0) {
              acc.push(curr);
              return acc;
            }
            const last = acc[acc.length - 1];
            // if this title is same as last title AND last item end date is same as the previous day end of day for the current events start date, then extend the last event
            if (
              last.title === curr.title &&
              last.end.getTime() >=
                endOfDay(sub(curr.start.getTime(), { days: 1 })).getTime()
            ) {
              last.end = curr.end;
            } else {
              // otherwise add a new event for this one
              acc.push(curr);
            }
            return acc;
          },
          []
        );
        return mergedDaysWithSameAvailability;
      }
    );
    return eventsGroupedByProduct;
  }

  private cacheProductsAndCategories(
    products: ProductCacheItem[],
    categories: CategoryCacheItem[],
    items: Item[]
  ) {
    return combineLatest([this.productCache$, this.categoryCache$]).pipe(
      mergeMap(([productCache, categoryCache]) => {
        products.reduce(
          (acc, product) => acc.set(product.id, product),
          productCache
        );
        categories.reduce(
          (acc, category) => acc.set(category.id, category),
          categoryCache
        );
        const missingProducts = new Set<number>(
          items
            .map(({ product_id }) => product_id)
            .filter((id) => id !== undefined && productCache.has(id) === false)
        );
        if (missingProducts.size) {
          return this.getMissingProducts(missingProducts, productCache).pipe(
            mergeMap((filledProductCache) =>
              this.getMissingCategories(filledProductCache, categoryCache).pipe(
                map((filledCategoryCache) => ({
                  productCache: filledProductCache,
                  categoryCache: filledCategoryCache
                }))
              )
            )
          );
        }
        return of({ productCache, categoryCache });
      })
    );
  }

  private getMissingProducts(
    missingProducts: Set<number>,
    productCache: Map<number, ProductCacheItem>
  ): Observable<Map<number, ProductCacheItem>> {
    return this.productService
      .getProductsById<ProductCacheItem>(
        Array.from(missingProducts),
        [],
        [],
        ['id', 'name', 'product_category_id']
      )
      .pipe(
        tap((result) =>
          result.forEach((product: ProductCacheItem) =>
            productCache.set(product.id, product)
          )
        ),
        mapTo(productCache)
      );
  }

  private getMissingCategories(
    pc,
    categoryCache: Map<number, CategoryCacheItem>
  ) {
    const missingCategories = new Set(
      Array.from(pc.values())
        .map(({ product_category_id }) => product_category_id)
        .filter((categoryId) => !!categoryId && !categoryCache.has(categoryId))
    );
    if (missingCategories.size) {
      return this.categoryService
        .getProductCategoriesById(
          Array.from(missingCategories),
          [],
          [],
          ['id', 'name']
        )
        .pipe(
          tap((result: Pick<ProductCategory, 'id' | 'name'>[]) =>
            result.forEach((category) =>
              categoryCache.set(category.id, category)
            )
          ),
          mapTo(categoryCache)
        );
    }
    return of(categoryCache);
  }

  private mapeEventToCalendarEvent(
    { start, title, data, end }: CombinedCalendarItem<any>,
    locations: Map<number, string>,
    color?: any
  ): CalendarEvent<MetaData<any>> {
    if (locations.size) {
      const locationId =
        data.type === 'hireItem'
          ? data.item.location_out
          : getBookingItemLocation(data.item);
      title += locations.has(locationId)
        ? ` (${locations.get(locationId)})`
        : '';
    }
    const classNames = this.getCssClassNames(data.type, data.transportJobs);
    return {
      meta: data,
      start: startOfDay(new Date(start)),
      title,
      end: end ? new Date(end) : null,
      allDay: true,
      color,
      actions: data.transportJobs.map((job) => this.transportJobToAction(job)),
      cssClass: classNames
    };
  }

  private fillInEventGaps(
    events: CalendarEvent<MetaData<any>>[],
    productCache: Map<number, ProductCacheItem>,
    locationMap: Map<number, string>
  ) {
    const groupedByItem = this.groupEventsByItem(events);
    const fillerEvents: CalendarEvent<MetaData<Item>>[] = [];
    for (const [id, calEvents] of groupedByItem) {
      const fillers = this.generateFillers(
        calEvents,
        productCache,
        locationMap
      );
      fillerEvents.push(...fillers);
    }
    return fillerEvents;
  }

  private generateFillers(
    calEvents: CalendarEvent<MetaData<any>>[],
    productCache: Map<number, ProductCacheItem>,
    locationMap: Map<number, string>
  ): CalendarEvent<MetaData<Item>>[] {
    const sorted = calEvents.sort(
      ({ start: a }, { start: b }) => a.valueOf() - b.valueOf()
    );
    return sorted
      .map((current, i, all) => {
        const item = current.meta.item.item;
        const start = add(current.end, { days: 1 });
        const nextEvent = all[i + 1];
        const end = nextEvent
          ? add(nextEvent.start, { days: -1 })
          : add(new Date(), { weeks: 4 });
        return { start, end, item };
      })
      .filter(({ start, end }) => start.valueOf() <= end.valueOf())
      .map(({ start, end, item }) =>
        this.createAvailableEvent(
          start,
          end,
          item,
          this.buildAvailableItemTitle(item, productCache, locationMap)
        )
      );
  }

  private buildAvailableItemTitle(
    item: Item,
    productCache: Map<number, ProductCacheItem>,
    locations: Map<number, string>
  ) {
    let title =
      productCache && productCache.has(item.product_id)
        ? `${item.code} - ${productCache.get(item.product_id).name}`
        : item.code;

    if (locations.size && item.location) {
      title += ` (${locations.get(parseInt(item.location, 10))})`;
    }
    return title;
  }

  private createAvailableEvent(
    start: Date,
    end: Date,
    item,
    title
  ): CalendarEvent<MetaData<Item>> {
    const workshopJobAction: CalendarEventAction = {
      label: `<i class="material-icons mat-icon text-red" title="Workshop Job Outstanding">build</i>`,
      a11yLabel: 'Outstanding Workshop Jobs',
      onClick: (e) => null
    };
    return {
      start,
      end,
      allDay: true,
      title,
      color: AVAILABLE_COLOR,
      meta: {
        type: 'available',
        parent_id: item.product_id,
        item,
        transportJobs: [],
        overdue: false
      },
      actions: item?.hasCurrentWorkshopJobs ? [workshopJobAction] : undefined,
      cssClass: this.getCssClassNames('available')
    };
  }

  private createProductEvent(
    start: Date,
    end: Date,
    product: ProductCacheItem,
    itemIds: number[],
    title: string
  ): CalendarEvent<MetaData<ProductCacheItem>> {
    return {
      start,
      end,
      allDay: true,
      title,
      color: AVAILABLE_COLOR,
      meta: {
        type: 'product',
        parent_id: product?.id ?? -1,
        item: product,
        transportJobs: [],
        itemIds,
        overdue: false
      },
      cssClass: this.getCssClassNames('available')
    };
  }

  private groupEventsByItem(events: CalendarEvent<MetaData<any>>[]) {
    return events.reduce((acc, curr) => {
      const itemId = curr.meta.item.item_id;
      if (!itemId) {
        return acc;
      }
      const itemInMap = acc.get(itemId);
      if (itemInMap) {
        itemInMap.push(curr);
        return acc;
      }
      acc.set(itemId, [curr]);
      return acc;
    }, new Map<number, CalendarEvent<MetaData<any>>[]>());
  }

  private generateEventsForAvailableItems(
    availableItems: Item[],
    productCache: Map<number, ProductCacheItem>,
    locationMap: Map<number, string>
  ) {
    const start = new Date();
    const end = add(start, { weeks: 4 });
    return availableItems.map((item) =>
      this.createAvailableEvent(
        start,
        end,
        item,
        this.buildAvailableItemTitle(item, productCache, locationMap)
      )
    );
  }

  private tentativelyAllocateItems(
    _,
    available: CalendarEvent<MetaData<Item>>[]
  ) {
    // const allocated = noProduct.flatMap(bookingWithoutProduct => )
    // return available;
  }

  private getCalendarEventCategory(
    event: CalendarEvent<MetaData<any>>,
    productCache: Map<number, ProductCacheItem>
  ): number {
    switch (event.meta.type) {
      case 'hireItem':
        return event.meta.item.item.product.product_category_id ?? -1;
      case 'bookingItem':
        return event.meta.item.category_id ?? -1;
      case 'available':
        return (
          productCache.get(event.meta.item.product_id)?.product_category_id ??
          -1
        );
      case 'product':
        return event.meta.item?.product_category_id ?? -1;
    }
    return 0;
  }

  private transportJobToAction(job: Delivery): CalendarEventAction {
    const classes = this.getTransportJobClass(job);
    return {
      label: `<i class="material-icons mat-icon ${classes}">local_shipping</i>`,
      a11yLabel: 'Delivery',
      onClick: (e) => null
    };
  }

  private getTransportJobClass(job: Delivery) {
    let status = job?.fulfilled_date ? 'complete' : 'pending';
    if (
      !job.fulfilled_date &&
      new Date(job.due).valueOf() < new Date().valueOf()
    ) {
      status += ' overdue';
    }
    return status;
  }

  private getCalendarEventLocation(
    event: CalendarEvent<MetaData<any>>
  ): number {
    if (event.meta.type === 'hireItem') {
      return (event.meta.item as HireItem).location_out;
    }
    if (event.meta.type === 'bookingItem') {
      return (event.meta.item as BookingItem).location_id;
    }
    if (event.meta.type === 'available') {
      return parseInt(event.meta.item.location, 10);
    }
  }

  private getCssClassNames(type: EventType, transportJobs: Delivery[] = []) {
    let main = '';
    switch (type) {
      case 'hireItem':
        main = 'hire-item';
        break;
      case 'bookingItem':
        main = 'booking-item';
        break;
      case 'available':
        main = 'available';
        break;
    }
    return `${main} ${transportJobs.length ? 'transport' : 'no-transport'}`;
  }
}
